import React, { useCallback, useContext, useEffect, useState } from 'react';

import { connect } from 'react-redux';
import {
  ConfigurationFeatureFlag,
  ConfigurationModel,
  ConfigurationTheme,
  defaultConfiguration,
  getDefaultConfigurationTheme,
  ThemeCode,
} from '@tink/config/src/tinkLinkConfig';
import { TinkLinkUrlParameter } from '@tink/config/src/TinkLinkUrlParameter';
import { theming } from '@tink/tink-link-core/src/utilities/theming';
import { isMerchantNetflix } from '../../../models/configuration';
import {
  ConfigurationService,
  configurationService as configurationServiceImport,
  ERR_FAILED_TO_FETCH_CONFIGURATION,
} from '../../../services/configuration';
import { useUrlQuery } from '../UrlQueryProvider/UrlQueryProvider';
import { AppState, ConnectedPropsWithoutDispatch } from '../../../stores/redux-store';
import { setApplicationError as setApplicationErrorConnect } from '../../../stores/app-state-store';
import { getParameter } from '../../../utilities/urlParametersValidator';
import {
  ERR_FAILED_TO_FETCH_THEMES,
  ThemeService,
  themeService as themeServiceImport,
} from '../../../services/theme';
import { netflixTheme } from '../ConfiguredThemeProvider/netflixTheme';

export enum ConfigurationLoadingState {
  LOADED,
  LOADING,
}

export type ConfigurationContextProps = {
  data: ConfigurationModel;
  loadingState: ConfigurationLoadingState;
  activeTheme: ConfigurationTheme;
};

export const ConfigurationContext = React.createContext<ConfigurationContextProps>({
  data: defaultConfiguration,
  loadingState: ConfigurationLoadingState.LOADING,
  activeTheme: getDefaultConfigurationTheme(ThemeCode.LIGHT),
});

export const useConfiguration = () => useContext(ConfigurationContext);

export const ConfigurationIsLoading: React.FC = ({ children }) => {
  const configuration = useConfiguration();

  if (configuration.loadingState === ConfigurationLoadingState.LOADING) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return null;
};

type ConfigurationFeatureFlagWrapperProps = {
  children: (hasFeatureFlag: boolean) => React.ReactNode;
  featureFlag: ConfigurationFeatureFlag;
};

export const ConfigurationFeatureFlagWrapper: React.FC<ConfigurationFeatureFlagWrapperProps> = ({
  children,
  featureFlag,
}) => {
  const configuration = useConfiguration();

  if (configuration.loadingState === ConfigurationLoadingState.LOADING) {
    return null;
  }

  const hasFeatureFlag = configuration.data.featureFlags.includes(featureFlag);

  return <>{children(hasFeatureFlag)}</>;
};

export const getThemeByCode = (themes: ConfigurationTheme[], desiredThemeType: ThemeCode) => {
  const desiredTheme = themes.find((theme) => theme.type === desiredThemeType);
  const fallbackTheme = themes[0];

  return desiredTheme || fallbackTheme;
};

export const getThemeParameter = () => {
  const theme = getParameter(TinkLinkUrlParameter.THEME);

  if (theme && (theme === ThemeCode.DARK || theme === ThemeCode.LIGHT)) {
    return theme as ThemeCode;
  }

  return undefined;
};

type ConfigurationProviderProps = PropsFromRedux & {
  children: React.ReactNode;
  themeService?: ThemeService;
  configurationService?: ConfigurationService;
};

export const ConfigurationProviderPresenter = ({
  children,
  userAccessToken,
  themeService = themeServiceImport,
  configurationService = configurationServiceImport,
  setApplicationError,
  session,
}: ConfigurationProviderProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [configuration, setConfiguration] = useState(defaultConfiguration);
  const [activeTheme, setActiveTheme] = useState(getDefaultConfigurationTheme(ThemeCode.LIGHT));
  const { clientId, sessionId: sessionIdParam } = useUrlQuery();

  const fetchConfiguration = useCallback(async () => {
    try {
      let maybeConfiguration: ConfigurationModel | undefined;

      const merchantId = session && session.merchantId;

      if (merchantId) {
        maybeConfiguration = await configurationService.getConfigurationByMerchantId(merchantId);
      }

      if (!maybeConfiguration) {
        maybeConfiguration = await configurationService.getAppConfiguration(
          userAccessToken as string,
          clientId
        );
      }

      setConfiguration(maybeConfiguration);
    } catch (err) {
      setApplicationError(ERR_FAILED_TO_FETCH_CONFIGURATION);
    }
  }, [clientId, configurationService, session, setApplicationError, userAccessToken]);

  const setupActiveTheme = useCallback(async () => {
    try {
      let customThemes: ConfigurationTheme[] = [];

      const merchantId = session && session.merchantId;

      if (merchantId) {
        customThemes = await themeService.getThemesByMerchantId(merchantId);
      }

      if (customThemes.length === 0) {
        customThemes = await themeService.getAppThemes();
      }

      if (customThemes.length === 0) {
        return;
      }

      const themeQueryParameter = getThemeParameter();
      const themeType = themeQueryParameter || theming.resolveThemePreference();

      const theme = getThemeByCode(customThemes, themeType);

      setActiveTheme(theme);
    } catch (err) {
      setApplicationError(ERR_FAILED_TO_FETCH_THEMES);
    }
  }, [session, themeService, setApplicationError]);

  useEffect(() => {
    if (!userAccessToken) {
      return;
    }

    if (sessionIdParam && !session) {
      return;
    }

    const setupConfigAndTheme = async () => {
      await Promise.all([setupActiveTheme(), fetchConfiguration()]);

      setIsLoading(false);
    };

    setupConfigAndTheme();
  }, [setupActiveTheme, fetchConfiguration, userAccessToken, sessionIdParam, session]);

  return (
    <ConfigurationContext.Provider
      value={{
        data: configuration,
        activeTheme: isMerchantNetflix(configuration)
          ? {
              ...activeTheme,
              header: netflixTheme.header,
              textAlignment: netflixTheme.textAlignment,
              typography: netflixTheme.typography,
            }
          : activeTheme,
        loadingState: isLoading
          ? ConfigurationLoadingState.LOADING
          : ConfigurationLoadingState.LOADED,
      }}
    >
      {children}
    </ConfigurationContext.Provider>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    userAccessToken: state.user.accessToken,
    session: state.session.data,
  };
};

const mapDispatchToProps = {
  setApplicationError: setApplicationErrorConnect,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedPropsWithoutDispatch<typeof connector>;

export const ConfigurationProvider = connector(ConfigurationProviderPresenter);

export const Configuration = {
  hasFeatureFlag: (
    { featureFlags: configurationFeatureFlags }: ConfigurationModel,
    featureFlag: ConfigurationFeatureFlag
  ) => {
    return configurationFeatureFlags.includes(featureFlag);
  },
};
